import React, { useState } from "react";
import { useShow } from "@refinedev/core";
import {
  Tag,
  Card,
  Row,
  Col,
  Descriptions,
  Table,
  Spin,
  Button
} from "antd";
import { CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { BudgetValidationDialog } from "./components/BudgetValidationDialog";
import { ApprovalDialog } from "./components/ApprovalDialog";
import { SubmitButton } from "./components/SubmitButton";
import WorkflowDiagram from "../purchase-template/components/WorkflowDiagram";
import { PROCUREMENT_PLAN_ONE_QUERY } from "./queries";
import { UserInfo } from "../../components/UserInfo";
import { IProcurementPlan, IProcurementItem } from "./interface";

// Status badges for procurement plans
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusColors: Record<string, string> = {
    DRAFT: 'default',
    SUBMITTED: 'processing',
    UNDER_REVIEW: 'warning',
    APPROVED: 'success',
    REJECTED: 'error',
    PARTIALLY_APPROVED: 'purple',
    EXPIRED: 'red'
  };

  const statusText: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    UNDER_REVIEW: 'Under Review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    PARTIALLY_APPROVED: 'Partially Approved',
    EXPIRED: 'Expired'
  };

  return (
    <Tag color={statusColors[status] || 'default'}>
      {statusText[status] || status}
    </Tag>
  );
};

// Priority badges for items
const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const priorityColors: Record<string, string> = {
    LOW: 'default',
    MEDIUM: 'processing',
    HIGH: 'warning',
    URGENT: 'error'
  };

  return (
    <Tag color={priorityColors[priority] || 'default'}>
      {priority}
    </Tag>
  );
};

export const ShowProcurementPlan: React.FC = () => {
  const [isValidationDialogVisible, setIsValidationDialogVisible] = useState(false);
  const [isApprovalDialogVisible, setIsApprovalDialogVisible] = useState(false);
  
  const { query } = useShow<IProcurementPlan>({
    resource: "procurementPlans",
    meta: {
      gqlQuery: PROCUREMENT_PLAN_ONE_QUERY,
    },
  });
  const { data, isLoading } = query;
  const record = data?.data;

  const handleSubmitSuccess = () => {
    // Refresh the data after successful submission
    query.refetch();
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (!record) {
    return <div>Procurement plan not found</div>;
  }

  // Columns for the items table
  const itemColumns = [
    {
      title: 'Item Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Description',
      dataIndex: 'productDescription',
      key: 'productDescription',
    },
    {
      title: 'Type',
      dataIndex: 'categoryType',
      key: 'categoryType',
      render: (type: string) => {
        const labels: Record<string, string> = {
          GOODS: 'Goods',
          SERVICES: 'Services',
          WORKS: 'Works',
        };
        return labels[type] || type;
      },
    },
    {
      title: 'Category',
      dataIndex: 'productCategory',
      key: 'productCategory',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `ETB ${price.toLocaleString()}`
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => `ETB ${price.toLocaleString()}`
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      key: 'requiredDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : ''
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => <PriorityBadge priority={priority} />
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Procurement Plan Details</h1>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        {record.status === 'DRAFT' && (
          <div style={{ display: 'inline-block', marginRight: 8 }}>
            <SubmitButton
              planId={record.id}
              plan={record}
              buttonText="Submit"
              onSuccess={handleSubmitSuccess}
            />
          </div>
        )}
        {record.status === 'SUBMITTED' && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => setIsValidationDialogVisible(true)}
            style={{ marginRight: 8 }}
          >
            Validate Budget
          </Button>
        )}
        {record.status === 'VALIDATED' && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => setIsApprovalDialogVisible(true)}
          >
            Approve Plan
          </Button>
        )}
      </div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Plan Information">
            <Descriptions column={2} layout="vertical" bordered>
              <Descriptions.Item label="Plan Number">
                {record.planNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Title">
                {record.title}
              </Descriptions.Item>
              <Descriptions.Item label="Team">
                {record.teamName}
              </Descriptions.Item>
              <Descriptions.Item label="Fiscal Year">
                {record.fiscalYear}
              </Descriptions.Item>
              <Descriptions.Item label="Planning Period">
                {record.planningPeriod}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusBadge status={record.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                ETB {record.totalAmount.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Submission Deadline">
                {dayjs(record.submissionDeadline).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {record.description}
              </Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {record.notes}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Procurement Items">
            <Table
              dataSource={record.items}
              columns={itemColumns}
              rowKey="id"
              pagination={false}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <strong>ETB {record.items.reduce((sum: number, item: IProcurementItem) => sum + item.totalPrice, 0).toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={3}></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
      </Row>
      
      <BudgetValidationDialog
        visible={isValidationDialogVisible}
        onClose={() => setIsValidationDialogVisible(false)}
        planId={record.id}
        totalAmount={record.totalAmount}
        onValidationSuccess={() => {
          // Refresh the data after successful validation
          query.refetch();
        }}
      />
      
      <ApprovalDialog
        visible={isApprovalDialogVisible}
        onClose={() => setIsApprovalDialogVisible(false)}
        plan={record}
        onApprovalSuccess={() => {
          // Refresh the data after successful approval
          query.refetch();
        }}
      />
      {/* Budget Validation and Approval Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={12}>
          <Card title="Budget Validation" bordered>
            {record.budgetValidation ? (
              <Descriptions column={1} layout="vertical" bordered>
                <Descriptions.Item label="Validated At">
                  {record.budgetValidation.validatedAt ? dayjs(record.budgetValidation.validatedAt).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </Descriptions.Item>
                 <Descriptions.Item label="Validated By">
                  {record.budgetValidation.validatedBy ? (
                    <UserInfo userId={record.budgetValidation.validatedBy} />
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Total Amount">
                  ETB {record.budgetValidation.totalAmount?.toLocaleString() || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Remarks">
                  {record.budgetValidation.remarks || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <div>No budget validation data available.</div>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Approval" bordered>
            {record.approval ? (
              <Descriptions column={1} layout="vertical" bordered>
                 <Descriptions.Item label="Approved By">
                  {record.approval.approvedBy ? (
                    <UserInfo userId={record.approval.approvedBy} />
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Approved On">
                  {record.approval.approvedOn ? dayjs(record.approval.approvedOn).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Remarks">
                  {record.approval.remarks || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <div>No approval data available.</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Approval Workflow Diagram */}
      {record.approvalType === 'EXTERNAL' && record.externalWorkflow && (
        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <WorkflowDiagram flow={record.externalWorkflow} />
          </Col>
        </Row>
      )}
    </div>
  );
};